import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Music, Sparkles, CheckCircle2, X, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const inviteTranslations = {
  uk: {
    badge: 'Персональне запрошення • VeonFox Music',
    title: 'Кабінет артиста',
    subtitle: 'Створюйте та діліться музикою',
    message: (name, isOlexandr) => isOlexandr 
      ? `Олександре! Команда VeonFox персонально запрошує вас та вашу рок-групу MustHaveUAGrunge приєднатися до нашої нової стримінгової платформи як офіційний верифікований виконавець!`
      : `${name}! Команда VeonFox персонально запрошує вас приєднатися до нашої нової стримінгової платформи в якості офіційного верифікованого виконавця!`,
    feature1: 'Офіційна синя галочка Verified Artist у музичному розділі',
    feature2: 'Пряме завантаження треків без обмежень до нашого аудіокаталогу',
    feature3: 'Детальна аналітика прослуховувань, збережень та унікальних слухачів',
    btnAccept: 'Прийняти запрошення',
    btnLater: 'Пізніше'
  },
  ru: {
    badge: 'Персональное приглашение • VeonFox Music',
    title: 'Кабинет исполнителя',
    subtitle: 'Создавайте и делитесь музыкой',
    message: (name, isOlexandr) => isOlexandr 
      ? `Александр! Команда VeonFox персонально приглашает вас и вашу рок-групу MustHaveUAGrunge присоединиться к нашей новой стриминговой платформе в качестве официального верифицированного исполнителя!`
      : `${name}! Команда VeonFox персонально приглашает вас присоединиться к нашей новой стриминговой платформе в качестве официального верифицированного исполнителя!`,
    feature1: 'Официальная синяя галочка Verified Artist в музыкальном разделе',
    feature2: 'Прямая загрузка треков без лимитов в наш аудиокаталог',
    feature3: 'Детальная аналитика прослушиваний, сохранений и уникальных слушателей',
    btnAccept: 'Принять приглашение',
    btnLater: 'Позже'
  },
  en: {
    badge: 'Personal Invitation • VeonFox Music',
    title: 'Artist Dashboard',
    subtitle: 'Create and share your sound',
    message: (name, isOlexandr) => isOlexandr 
      ? `Olexandr! The VeonFox team personally invites you and your rock band MustHaveUAGrunge to join our brand new streaming platform as a verified music artist!`
      : `${name}! The VeonFox team personally invites you to join our brand new streaming platform as a verified music artist!`,
    feature1: 'Official blue checkmark (Verified Artist) in the Music catalog',
    feature2: 'Direct track uploads with high-quality audio files',
    feature3: 'In-depth analytics, listening stats, saves, and fan counts',
    btnAccept: 'Accept & Claim Profile',
    btnLater: 'Later'
  }
};

export default function ArtistInviteModal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const currentLang = i18n.language || 'en';
  const langKey = currentLang.startsWith('uk') ? 'uk' : currentLang.startsWith('ru') ? 'ru' : 'en';
  const tLocal = inviteTranslations[langKey];

  useEffect(() => {
    if (user?.id) {
      const username = (user.username || '').toLowerCase();
      const displayName = (user.displayName || '').toLowerCase();
      
      const isTargeted = username.includes('aparhom') || 
                         displayName.includes('olexandr') || 
                         username.includes('artur') || 
                         displayName.includes('artur') || 
                         displayName.includes('mikolaev') ||
                         username.includes('vilan') ||
                         displayName.includes('vilan') ||
                         user.isArtistInvited === true || 
                         user.artistInviteTarget === true;

      if (isTargeted && !user.isArtist) {
        const hasDismissed = localStorage.getItem('artist_invite_dismissed_' + user.id);
        if (!hasDismissed) {
          setIsOpen(true);
        }
      }
    }
  }, [user]);

  const handleAccept = () => {
    if (user?.id) {
      localStorage.setItem('artist_invite_dismissed_' + user.id, 'true');
    }
    setIsOpen(false);
    navigate('/music', { state: { openArtistPortal: true } });
  };

  const handleClose = () => {
    if (user?.id) {
      localStorage.setItem('artist_invite_dismissed_' + user.id, 'true');
    }
    setIsOpen(false);
  };

  if (!isOpen) return null;

  const displayName = user?.displayName || 'Creator';
  const isOlexandr = displayName.toLowerCase().includes('olexandr') || (user?.username || '').toLowerCase().includes('aparhom');

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/80 backdrop-blur-md"
          onClick={handleClose}
        />

        <motion.div 
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", stiffness: 350, damping: 25 }}
          className="relative bg-gradient-to-br from-zinc-950 via-zinc-900 to-black border border-emerald-500/30 rounded-3xl w-full max-w-lg shadow-[0_0_50px_rgba(16,185,129,0.15)] p-6 sm:p-8 overflow-hidden text-left"
        >
          <div className="absolute -right-16 -top-16 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -left-16 -bottom-16 w-48 h-48 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />

          <button 
            onClick={handleClose}
            className="absolute top-5 right-5 w-8 h-8 rounded-full bg-zinc-850 hover:bg-zinc-800 text-zinc-400 hover:text-white flex items-center justify-center transition-all z-10"
          >
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-emerald-500 to-teal-400 flex items-center justify-center text-black font-black shadow-lg shadow-emerald-500/20 relative">
              <Music className="w-6 h-6 stroke-[2.5]" />
              <Sparkles className="w-4 h-4 text-white absolute -top-1 -right-1 animate-pulse" />
            </div>
            <div>
              <span className="text-[9px] font-black uppercase tracking-widest text-emerald-400 bg-emerald-950/60 px-2.5 py-0.5 rounded-full border border-emerald-500/20">
                {tLocal.badge}
              </span>
              <h2 className="text-xl sm:text-2xl font-black text-white font-unbounded mt-1">
                {tLocal.title}
              </h2>
            </div>
          </div>

          <div className="space-y-4 mb-8">
            <p className="text-sm text-zinc-300 font-semibold leading-relaxed">
              {tLocal.message(displayName, isOlexandr)}
            </p>

            <div className="bg-zinc-900/80 rounded-2xl border border-zinc-800 p-4 space-y-2.5">
              <div className="flex items-center gap-2.5 text-xs text-zinc-200 font-semibold">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>{tLocal.feature1}</span>
              </div>
              <div className="flex items-center gap-2.5 text-xs text-zinc-200 font-semibold">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>{tLocal.feature2}</span>
              </div>
              <div className="flex items-center gap-2.5 text-xs text-zinc-200 font-semibold">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>{tLocal.feature3}</span>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3">
            <button 
              onClick={handleAccept}
              className="w-full sm:flex-1 py-3.5 px-6 bg-gradient-to-r from-emerald-500 to-teal-400 hover:from-emerald-400 hover:to-teal-300 active:scale-[0.98] text-black font-extrabold text-xs uppercase tracking-wider rounded-2xl transition-all shadow-lg shadow-emerald-500/25 flex items-center justify-center gap-2"
            >
              <span>{tLocal.btnAccept}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <button 
              onClick={handleClose}
              className="w-full sm:w-auto py-3.5 px-5 bg-zinc-900 hover:bg-zinc-850 active:scale-[0.98] text-zinc-400 hover:text-white font-bold text-xs rounded-2xl border border-zinc-800 transition-all text-center"
            >
              {tLocal.btnLater}
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
